import {
  Car,
  Accessibility,
  MapPin,
  Ruler,
  Layers,
  ArrowUpDown,
  Info,
} from 'lucide-react';

/*
 * AccessibleParking — location-specific accessible parking locator, shown
 * on each campsite detail page.  Same "estate warden calmly explaining"
 * tone and high-contrast style as SafetyPanel, so the two sections sit
 * comfortably beside one another.
 */

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-3 border-t border-[var(--brand-forest-deep)]/12 first:border-t-0">
      <span
        aria-hidden="true"
        className="shrink-0 w-9 h-9 grid place-items-center bg-[var(--brand-forest-deep)] text-[var(--brand-gold)] border-2 border-[var(--brand-gold)]"
      >
        <Icon size={16} strokeWidth={2.25} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[var(--brand-forest-deep)]">
          {label}
        </div>
        <p
          className="mt-1 text-sm sm:text-[15px] text-[var(--brand-ink)] leading-relaxed font-medium"
          dangerouslySetInnerHTML={{ __html: value }}
        />
      </div>
    </div>
  );
}

export default function AccessibleParking({ site }) {
  const parking = site?.accessibleParking;
  if (!parking) return null;

  return (
    <section
      data-testid="campsite-accessible-parking"
      className="bg-[var(--brand-parchment)]"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 sm:py-16">
        {/* Header — car + accessibility icons together */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold text-[var(--brand-forest-deep)] bg-[var(--brand-gold)] px-3 py-1.5 border-2 border-[var(--brand-forest-deep)]">
            <Car size={14} strokeWidth={2.5} />
            <Accessibility size={14} strokeWidth={2.5} />
            Accessible Parking Locator
          </span>
          <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--brand-olive)]">
            {site.region}
          </span>
        </div>

        <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[var(--brand-forest-deep)] leading-tight">
          Nearest accessible parking &mdash;{' '}
          <span className="italic text-[var(--brand-forest)]">
            park close, ride easy.
          </span>
        </h2>
        <p
          data-testid="accessible-parking-intro"
          className="mt-4 text-base sm:text-lg text-[var(--brand-ink)] leading-relaxed max-w-3xl font-medium"
        >
          A wee brief on the closest accessible parking to this pitch &mdash;
          how far, what surface, and whether there&rsquo;s a height barrier to
          worry about. Handy to check on the phone before you set off.
        </p>

        {/* Info card */}
        <div className="mt-8 bg-[var(--brand-surface)] border-2 border-[var(--brand-forest-deep)]/15 border-l-8 border-l-[var(--brand-gold)] px-5 py-5 sm:px-7 sm:py-6">
          {/* Location header */}
          <div className="flex items-start gap-4 pb-4 border-b-2 border-[var(--brand-forest-deep)]/10">
            <span
              aria-hidden="true"
              className="shrink-0 w-14 h-14 grid place-items-center bg-[var(--brand-forest-deep)] text-[var(--brand-gold)] border-2 border-[var(--brand-gold)]"
            >
              <Accessibility size={26} strokeWidth={2.25} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--brand-forest-deep)]">
                Nearest accessible bay
              </div>
              <div className="mt-1 font-display text-3xl sm:text-4xl font-semibold text-[var(--brand-forest-deep)] leading-tight">
                {parking.name}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] uppercase tracking-[0.16em] font-semibold text-[var(--brand-olive)]">
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={12} strokeWidth={2.5} />
                  {parking.postcode}
                </span>
                <span className="opacity-40">·</span>
                <span>{parking.distanceToTrailhead}</span>
              </div>
            </div>
          </div>

          {/* Info rows */}
          <div className="mt-2">
            <InfoRow
              icon={Layers}
              label="Surface"
              value={parking.surface}
            />
            <InfoRow
              icon={Ruler}
              label="Bay width"
              value={parking.bayWidth}
            />
            <InfoRow
              icon={ArrowUpDown}
              label="Height barrier &amp; gate access"
              value={parking.heightBarrier}
            />
            {parking.notes && (
              <InfoRow
                icon={Info}
                label="Local notes"
                value={parking.notes}
              />
            )}
          </div>
        </div>

        {/* Seasonal-conditions reminder — warm forest band, matches Safety */}
        <div className="mt-6 flex items-start gap-3 bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)] px-5 py-4 border-l-8 border-[var(--brand-gold)]">
          <span
            aria-hidden="true"
            className="shrink-0 w-8 h-8 grid place-items-center bg-[var(--brand-gold)] text-[var(--brand-forest-deep)]"
          >
            <Info size={16} strokeWidth={2.5} />
          </span>
          <p
            data-testid="accessible-parking-seasonal-note"
            className="text-sm sm:text-base font-medium leading-relaxed"
          >
            Always check current conditions before travelling &mdash;{' '}
            <span className="font-bold text-[var(--brand-gold)]">
              tracks and car parks can change seasonally.
            </span>{' '}
            A quick ring to the local ranger service (listed on the Coming Home
            guide) is the surest way to know what you&rsquo;ll arrive to.
          </p>
        </div>
      </div>
    </section>
  );
}
